import {
  Button, DataSource, Label, Panel, Tabset, Textbox, Tree, ComponentTypes, Container, DataSourceProps, DragListener, ButtonVariant,cloneComponent, Snackbar, deserializeComponents,
  serializeComponents, TableCell, Layout, Component, TreeNode, DesignerInterface, DesignableObject, Splitter, KeyEvent, Table, KeyHandler, TabCloseEvent } from "@mcleod/components";
import { Alignment, setClassIncluded, getLogger, getThemeColor, Keys, DynamicLoader, Navigation, AuthType, StringUtil, Api, MetadataField, HorizontalAlignment, NavigationEvent, Model, ModelSearchResult, ModelRow } from "@mcleod/core";
import { CodeEditor } from "../common/CodeEditor";
import { ActionAddComponent } from "./actions/ActionAddComponent";
import { PropertiesTable } from "./PropertiesTable";
import { doDesignerAction, getDesignerRedoAction, getDesignerUndoAction, redoDesignerAction, undoDesignerAction } from "./UIDesignerActionHistory";
import { designerApplyChangeToSelectedComponents, designerClasses, designerHandleComponentSelection } from "./UIDesignerUtil";
import { DesignerTool } from "./DesignerTool";
import { DesignerTab } from "./actions/DesignerTab";
import { DesignerDataSource } from "./DesignerDataSource";
import { DesignerToolsPanel } from "./DesignerToolsPanel";
import { ExtendedDataType } from "@mcleod/core/src/ApiMetadata";
import { DropTargetPanel } from "@mcleod/components/src/components/panel/DropTargetPanel";
import { PanelOpenLayout } from "./PanelOpenLayout";
import { getDesignerKeyListeners } from "./UIDesignerKeyHandlers";
import { ReflectiveDialogs } from "@mcleod/components/src/base/ReflectiveDialogs";

const log = getLogger("designer.ui.UIDesigner");

interface DesignerContainer extends Container {
  acceptsTool(tool: DesignerTool): boolean;
}

interface OpenDescriptor {
  name: string;
  requireBase: boolean;
}

export class UIDesigner extends Layout implements DesignerInterface {
  tableProps: PropertiesTable;
  copiedComponents: Component[];
  containerDragOverListener: DragListener;
  containerDragDropListener: DragListener;
  selectedTool: DesignerTool;
  inputTableSearch: Textbox;
  inputFieldSearch: Textbox;
  tabset: Tabset;
  buttonViewCode: Button;
  buttonViewLayout: Button;
  buttonRun: Button;
  buttonOpen: Button;
  buttonSave: Button;
  buttonNew: Button;
  buttonUndo: Button;
  buttonRedo: Button;
  toolsPanel: DesignerToolsPanel;
  selectedComponents: DesignableObject[];
  selectedToolProps: any;
  finishedLoading: boolean;
  open: string; // passed as props from URL
  openBase: string; // passed as props from URL
  lastChangedProp: string;
  splitterProps: Splitter;
  splitterTools: Splitter;

  constructor(props?) {
    super({ auth: AuthType.LME, title: "UI Designer", fillHeight: true, scrollX: true, needsServerLayout: false, ...props });
    this.tableProps = new PropertiesTable(this);
    log.debug("Render designer");
    this.copiedComponents = [];
    this.setProps({ title: "UI Designer", auth: [AuthType.ANY], fillRow: true, fillHeight: true, padding: 0 });
    this.toolsPanel = new DesignerToolsPanel(this, { fillRow: true });
    this.tabset = new Tabset({ fillRow: true, fillHeight: true, rowBreak: false, allowStyleChange: false });
    this.splitterTools = new Splitter({ id: "splitterTools", position: 240, fillRow: true, fillHeight: true, expandButtonsVisible: false });
    this.splitterProps = new Splitter({ id: "splitterProps", position: "80%", fillRow: true, fillHeight: true, expandButtonsVisible: false });
    this.splitterTools.add(this.toolsPanel);
    this.splitterTools.add(this.splitterProps);
    this.add(this.splitterTools);
    this.splitterProps.add(this.tabset);
    this.addTabsetToolbar();
    const panelProps = new Panel({ fillRow: true, height: "100%", padding: 0, borderLeftWidth: 1, borderLeftColor: "strokeSecondary" });
    panelProps.add(new Label({ caption: "Properties", backgroundColor: "primary", align: HorizontalAlignment.CENTER, fillRow: true, color: "primary.reverse" }));
    panelProps.add(this.tableProps);
    this.splitterProps.add(panelProps);
    this.createContainerDragListeners();

    Navigation.addNavigationListener({
      onNavigate: async (event: NavigationEvent) => {
        const isNewTab = event.navOptions != null && event.navOptions.newTab === true;
        if (this.isModified() && !isNewTab) {
          if (event.navOptions?.hardRefresh || !await ReflectiveDialogs.showYesNo("Are you sure you want to leave this page without saving?", "Close Without Saving?"))
            event.preventDefault();
        }
      }
    });

    this.tabset.addAfterTabSelectionListener(event => this.tabChanged(event.newSelection));
    this.tabset.addBeforeTabCloseListener(event => this.tabClosed(event));
    this.selectedComponents = [];
    const open = this.getLastOpen();
    if (open.length === 0 && this.open == null)
      this.openTab(null, true, true);
    else {
      const lastSel = this.getLastSelected();
      for (let i = 0; i < open.length; i++)
        this.openTab(open[i].name, open[i].requireBase, open[i].name === lastSel?.name && open[i].requireBase == lastSel?.requireBase);
    }
    this.finishedLoading = true;
    if (this.open != null)
      this.openTab(this.open, this.openBase === "true", true);
  }

  addTabsetToolbar() {
    const toolsProps = { variant: ButtonVariant.round, color: "subtle.darker", dropped: false };
    this.buttonViewCode = new Button({ ...toolsProps, tooltip: "View code for this page", imageName: "curlyBrackets" });
    this.buttonViewCode.addClickListener(event => this.showCode());
    this.buttonViewLayout = new Button({ ...toolsProps, tooltip: "View raw page layout file", imageName: "codeTags" });
    this.buttonViewLayout.addClickListener(event => this.showJson());
    this.buttonRun = new Button({ ...toolsProps, tooltip: "Run this page", imageName: "run" });
    this.buttonRun.addClickListener(event => this.showTest());
    this.buttonOpen = new Button({ ...toolsProps, tooltip: "Open a page for editing", imageName: "folder" });
    this.buttonOpen.addClickListener(event => this.showOpen());
    this.buttonSave = new Button({ ...toolsProps, tooltip: "Save this page", imageName: "disk" });
    this.buttonSave.addClickListener(event => this.showSave());
    this.buttonNew = new Button({ ...toolsProps, tooltip: "Create a new page", imageName: "add" });
    this.buttonNew.addClickListener(event => this.addNewTab());
    this.buttonUndo = new Button({ ...toolsProps, tooltip: "Undo change", enabled: false, imageName: "undo" });
    this.buttonUndo.addClickListener(event => undoDesignerAction(this));
    this.buttonRedo = new Button({ ...toolsProps, tooltip: "Redo change", enabled: false, imageName: "redo" });
    this.buttonRedo.addClickListener(event => redoDesignerAction(this));
    this.tabset.addTool(this.buttonViewCode);
    this.tabset.addTool(this.buttonViewLayout);
    this.tabset.addTool(this.buttonRun);
    this.tabset.addTool(this.buttonOpen);
    this.tabset.addTool(this.buttonSave);
    this.tabset.addTool(this.buttonNew);
    this.tabset.addTool(this.buttonUndo);
    this.tabset.addTool(this.buttonRedo);
  }

  get firstSelected(): Component {
    if (this.selectedComponents.length === 0)
      return null;
    return this.selectedComponents[0] as Component;
  }

  // function called by router that can affect the properties of the router
  getRouterProps() {
    return { padding: 0 };
  }

  createContainerDragListeners() {
    this.containerDragOverListener = (event) => {
      if (event.target.acceptsTool(this.selectedTool))
        event.preventDefault();
    };
    this.containerDragDropListener = (event) => {
      let container = event.target;
      if (!container._isCompoundComponent) {
        if (container instanceof DropTargetPanel) {
          container = container.getSpecialDesignerDropTarget();
        }
        if (container != null) {
          const tool = this.selectedTool.toolName;
          this.toolDropped(tool, container, this.selectedToolProps);
        }
      }
    };
  }

  async getResourceInfo(): Promise<ModelRow> {
    const response = await Model.search("resource/info", {
      resource_name: this.getActiveTab().path + ".layout",
      resource_type: "layouts"
    });
    return response.getSingleModelRow();
  }

  async getCodeFileName(): Promise<string> {
    return (await this.getResourceInfo()).get("typescript_path");
  }

  addEventHandlerFunction(component: DesignableObject, eventName: string): void {
    const prop = component.getPropertyDefinitions()[eventName];
    if (prop == null)
      return;
    if (component instanceof DesignerDataSource)
      component = component.designerDataSource
    let signature = prop.eventSignature;
    if (signature == null)
      signature = eventName[0].toUpperCase() + eventName.substring(1) + "(event)";
    component[eventName] = component.id + StringUtil.stringBefore(signature, "(");
    this.redisplayProp(eventName, component[eventName]);
    this.showSave(true, false).then(async obj => {
      const fileName = await this.getCodeFileName();
      CodeEditor.addCodeFunction(fileName, component.id + signature, {
        contentsIfEmpty: this.getCodeSkeleton(),
        comment: "  /** This is an event handler for the " + eventName + " event of " + component.id + ".  */\n"
      });
    });
  }

  displayProperties() {
    this.tableProps.displayProperties(this.selectedComponents);
  }

  showCode() {
    this.showSave(true).then(async () => {
      const fileName = await this.getCodeFileName();
      CodeEditor.openCodeEditor(fileName, { contentsIfEmpty: this.getCodeSkeleton() })
    });
  }

  getCodeSkeleton() {
    const className = StringUtil.stringAfterLast(this.getActiveTab().path, "/");
    return `import { AutogenLayout${className} } from "./autogen/AutogenLayout${className}";

export class ${className} extends AutogenLayout${className} {
}
`;
  }

  async showJson() {
    const path = (await this.getResourceInfo()).get("resource_path");
    CodeEditor.openCodeEditor(path);
  }

  override getKeyHandlers(): KeyHandler[] {
    const result = [ ...getDesignerKeyListeners(this) ];
    result.push({ key: Keys.P, modifiers: { ctrlKey: true }, listener: () => {
      if (this.selectedComponents != null && this.selectedComponents.length === 1 && !(this.firstSelected.parent instanceof DesignerTab))
        this.selectComponent(this.firstSelected.parent);
    }});
    result.push({ key: Keys.I, modifiers: { ctrlKey: true }, listener: () => {
      if (this.selectedComponents != null)
        this.tableProps.applyKeyToProp(null, "id");
    }});
    result.push({ key: Keys.D, modifiers: { ctrlKey: true }, listener: () => {
      if (this.selectedComponents != null)
        this.tableProps.applyKeyToProp(null, "dataSource");
    }});
    result.push({ key: Keys.S, modifiers: { altKey: true }, listener: () => {
      this.saveActiveTab();
    }});
    result.push({ key: Keys.N, modifiers: { altKey: true }, listener: () => {
      this.addNewTab();
    }});
    result.push({ key: Keys.O, modifiers: { altKey: true }, listener: () => {
      this.showOpen();
    }});
    result.push({ key: Keys.W, modifiers: { altKey: true }, listener: () => {
      this.selectWidthProp();
    }});
    result.push({ key: Keys.P, modifiers: { shiftKey: true, ctrlKey: true }, listener: () => {
      this.addToolToContainer(new DesignerTool(this, "Panel"));
    }});
    result.push({ key: "ALL", listener: event => this.handleOtherKeys(event) });
    return result;
  }

  tabChanged(tab: DesignerTab) {
    if (this.finishedLoading === true)
      this.storeLastSelectedTab();
    if (tab == null)
      this.selectComponent(null);
    else
      this.selectComponent(tab.designerPanel);
    this.toolsPanel.displayDataSourceTools();
    this.displayProperties();
  }

  storeLastSelectedTab() {
    const tab = this.getActiveTab();
    if (tab == null)
      localStorage.removeItem("designer.ui.selected");
    else
      localStorage.setItem("designer.ui.selected", JSON.stringify({name: tab.path, requireBase: tab.baseVersion}));
  }

  selectWidthProp() {
    this.lastChangedProp = "width";
  }

  turnOnDraggable() {
    this.modified();
    this.firstSelected.draggable = true;
  }

  handleOtherKeys(event: KeyEvent): boolean {
    if (document.activeElement === document.body && !event.ctrlKey && !event.altKey) {
      const key = event.key;
      if (typeof key === "string" && key.length === 1 && key != "+" && key != "-") {
        this.tableProps.applyKeyToProp(key, this.lastChangedProp);
        return;
      }
    }
    event.shouldAutomaticallyStopPropagation = false;
  }

  getToolPropsForField(field: MetadataField) {
    const result: any = {};
    if (field != null) {
      result.field = field.name;
      result.name = field.name;
      if (field.dataType === "bool")
        result.toolType = "Checkbox";
      else if (field.dataType === "list")
        result.toolType = "Table";
      else if (field.extDataType === ExtendedDataType.CITY)
        result.toolType = "CityState";

      result.dataSource = this.getActiveTab().lastSelectedDataSource;
      result.caption = field.caption;
      result.toolDropped = false;
    }
    return result;
  }

  getActiveTab(): DesignerTab {
    return this.tabset.getActiveTab() as DesignerTab;
  }

  showSave(createBaseTS: boolean = false, baseTsOnly: boolean = false) {
    const tab = this.getActiveTab();
    if (tab.path == null) {
      return Api.search("layout/list").then(response => {
        const tree = new Tree({ height: 300, width: 400, borderWidth: 1, borderRadius: 4, borderColor: "strokeSecondary", nodeLeafImageName: "folder" });
        const node = tree.makeTreeNodesFromObject(response.data[0], "name", "children");
        this.removeLeafNodes(node);
        node.expanded = true;
        tree.getRootNode().setChildren(node.getChildren());
        const label = new Label({ caption: "Select save folder", fontBold: true });
        const savePath = new Textbox({ caption: "Layout name", required: true, fillRow: true });
        const panel = new Panel({ components: [label, tree, savePath] });
        return ReflectiveDialogs.showDialog(panel, { title: "Save Layout" }).then(() => {
          const tab = this.getActiveTab();
          const sel = tree.selectedNode;
          if (sel == null) {
            tree.showTooltip("You must select a folder to save this layout.", {
              timeout: 3000, shaking: true, position: Alignment.RIGHT
            });
            return false;
          }
          let path = "";
          for (const n of sel.path)
            path += n.caption + "/";
          path += savePath.text;
          tab.path = path;
          tab.caption = this.getTabTitle(tab.path);
          this.storeLastSelectedTab();
          this.saveActiveTab(createBaseTS, baseTsOnly);
        });
      });
    }
    else
      return this.saveActiveTab(createBaseTS, baseTsOnly);
  }

  removeLeafNodes(node: TreeNode) {
    for (let i = node.getChildCount() - 1; i >= 0; i--) {
      const child = node.getChild(i);
      if (child.getChildCount() === 0)
        node.removeChild(i);
      else
        this.removeLeafNodes(child);
    }
  }

  showTest() {
    let path = this.getActiveTab()?.path;
    if (path != null)
      path = StringUtil.stringAfter(path, "/"); // the path element should be the api project name, so skip it
    Navigation.navigateTo(path, { newTab: true });
  }


  showOpen() {
    const pnl = new PanelOpenLayout();
    ReflectiveDialogs.showDialog(pnl, { title: "Open Layout", height: 600, width: 500 }).then(() => {
      let path = "";
      pnl.tree?.selectedNode.path.forEach((node) => { path += node.caption + "/"; });
      const baseVersion: boolean = pnl.tree?.selectedNode?.data?.base_version;
      path = path.substring(0, path.length - 1);
      this.openTab(path, baseVersion, true);
    });
  }

  async saveActiveTab(createBaseTS: boolean = false, baseTSOnly: boolean = false) {
    const tab = this.getActiveTab();
    const body = { path: tab.path, require_base_version: tab.baseVersion, create_base_ts: createBaseTS, base_ts_only: baseTSOnly, definition: serializeComponents(tab.designerPanel, tab.dataSources) };
    return Api.post("layout", body).then((response) => {
      if (baseTSOnly !== true) {
        this.addLastOpen({ name: tab.path, requireBase: tab.baseVersion });
        Snackbar.showSnackbar(response.data[0].response);
        tab.modified = false;
      }
    }).catch(reason => ReflectiveDialogs.showError(reason));
  }

  getLastSelected(): OpenDescriptor {
    try {
      return JSON.parse(localStorage.getItem("designer.ui.selected"));
    } catch {
      localStorage.removeItem("designer.ui.selected");
      return null;
    }
  }

  getLastOpen(): OpenDescriptor[] {
    const open = localStorage.getItem("designer.ui.open");
    if (open == null || open.length === 0)
      return [];
    else
      try {
        return JSON.parse(open);
      } catch {
        localStorage.removeItem("designer.ui.open");
        return [];
      }
  }

  addLastOpen(entry: OpenDescriptor) {
    const index = this.getLastOpenIndex(entry);
    if (index < 0) {
      const lastOpen = this.getLastOpen();
      lastOpen.push(entry);
      localStorage.setItem("designer.ui.open", JSON.stringify(lastOpen));
    }
  }

  modified() {
    this.getActiveTab().modified = true;
  }

  isModified() {
    for (const tab of this.tabset.components)
      if ((tab as DesignerTab).modified)
        return true;
    return false;
  }

  applyChangeToSelectedComponents(data, newValue) {
    this.modified();
    designerApplyChangeToSelectedComponents(this.selectedComponents, this.getActiveTab(), data, newValue, this.tableProps);
  }

  addNewTab() {
    const tab = this.openTab(null, true, true);
    this.selectComponent(tab.designerPanel);
    this.displayProperties();
  }

  openTab(path: string, requireBaseVersion: boolean, selectTab: boolean): DesignerTab | undefined {
    if (path != null) {
      for (const tab of this.tabset) {
        if (tab.path === path && tab.baseVersion === requireBaseVersion) {
          tab.select();
          return;
        }
      }
    }

    const tab = new DesignerTab(this, path, { tabStyle: this.tabset.tabStyle, baseVersion: requireBaseVersion });
    let title = "New layout";
    if (path != null) {
      title = this.getTabTitle(path);
      Api.search("layout", { path: path, require_base_version: requireBaseVersion, apply_field_level_perms: false, apply_field_level_licensing: false }).then((response) => {
        const def = JSON.parse(response.data[0].response);
        tab.baseVersion = def["baseVersion"] === true;
        this.addLastOpen({ name: path, requireBase: tab.baseVersion });
        this.storeLastSelectedTab();
        if (def.dataSources != null)
          this.createDataSourcesFromDef(tab, def.dataSources, path);

        let owner = DynamicLoader.getClassForPath(path);
        if (owner != null)
          owner = new owner();
        let components = deserializeComponents(owner, def, this, { applyFieldLevelPermissions: false, applyFieldLevelLicensing: false }, tab.dataSources, null);
        for (const prop in tab.designerPanel.getPropertyDefinitions())
          if (def[prop] !== undefined && prop !== "mainDataSource") // Layout._deserializeSpecialProps handles mainDataSource
            tab.designerPanel[prop] = def[prop];
        tab.designerPanel.mainDataSource = components[0].mainDataSource; // this is embarassing
        components = components[0].components;
        for (let i = 0; i < components.length; i++)
          tab.designerPanel.add(components[i]);
        this.selectComponent(tab.designerPanel);
        this.displayProperties();
      }).catch(error => {
        let msg = error?.toString();
        const index = msg.indexOf("Could not find a layout ");
        if (index >= 0)
          msg = msg.substring(index, msg.indexOf("\n", index));
        tab.designerPanel.add(new Label({ caption: msg, fillRow: true, align: HorizontalAlignment.CENTER, marginTop: 32 }));
      });
    }
    tab.caption = title;
    tab._designer = this;
    this.tabset.add(tab);
    if (selectTab === true)
      this.tabset.selectedIndex = this.tabset.getComponentCount() - 1;
    return tab;
  }

  getOwnerForActiveTab() {
    const tab = this.getActiveTab();
    if (tab.owner == null) {
      const ownerFunc = DynamicLoader.getClassForPath(tab.path);
      if (ownerFunc != null)
        tab.owner = new ownerFunc();
    }
    return tab.owner;
  }

  createDataSourcesFromDef(tab: DesignerTab, sources: DataSourceProps[], layoutName: string): void {
    for (let i = 0; sources != null && i < sources.length; i++) {
      const dataSource = new DataSource({ ...sources[i] }, null, this, tab);
      dataSource.layoutName = layoutName;
      tab.dataSources[dataSource.id] = dataSource;
      tab.dataSourcePanel.targetPanel.add(new DesignerDataSource(this, dataSource));
    }
  }

  getTabTitle(path: string): string {
    const slashPos = path.lastIndexOf("/");
    if (slashPos >= 0)
      return path.substring(slashPos + 1);
    else
      return path;
  }

  async closeTab(tab: DesignerTab) {
    if (!tab._modified || await ReflectiveDialogs.showYesNo("Are you sure you want to close this tab without saving your changes?", "Close Without Saving?")) {
      tab.parent.remove(tab);
      if (this.tabset.getComponentCount() === 0)
        this.addNewTab();
    }
  }

  getLastOpenIndex(descriptor: OpenDescriptor) {
    const lastOpen:OpenDescriptor[] = this.getLastOpen();
    for (let i = 0; i < lastOpen.length; i++)
      if (lastOpen[i].name === descriptor.name && lastOpen[i].requireBase === descriptor.requireBase)
        return i;
    return -1;

  }

  tabClosed(event: TabCloseEvent) {
    const tab = event.tab as DesignerTab;
    if (tab.path != null) {
      const index = this.getLastOpenIndex({name: tab.path, requireBase: tab.baseVersion});
      if (index >= 0) {
          const lastOpen = this.getLastOpen();
          lastOpen.splice(index, 1);
          localStorage.setItem("designer.ui.open", JSON.stringify(lastOpen));
        }
    }
  }

  toolClicked(event, tool: DesignerTool) {
    this.selectTool(tool);
  }

  toolDblClicked(tool: DesignerTool, toolProps) {
    if (this.selectedComponents.length !== 1)
      Snackbar.showSnackbar("A parent component must be selected to add this tool to.");
    else
      this.addToolToContainer(tool, toolProps);
  }

  addToolToContainer(tool: DesignerTool, toolProps?) {
    this.selectTool(tool);
    this.selectedToolProps = toolProps;
    const selectedComponent = this.firstSelected;
    const toolName = this.selectedTool.toolName;
    const toolLowerCase = toolName.toLowerCase();
    const nextNumber = this.getNextNumber(toolLowerCase);
    const component = ComponentTypes.createComponentOfType(toolLowerCase, { id: toolLowerCase + nextNumber });
    if ((selectedComponent as any).acceptsTool != null && (selectedComponent as any).acceptsTool(this.selectedTool))
      doDesignerAction(this, new ActionAddComponent(component, (selectedComponent as Container)));
    else
      doDesignerAction(this, new ActionAddComponent(component, selectedComponent.parent));
    component._designer = this;
    this.getActiveTab()[toolLowerCase + nextNumber] = component;
    if ("caption" in component)
      component.caption = toolLowerCase + nextNumber;
    if (this.selectedToolProps != null)
      component.setProps(this.selectedToolProps);
    this.selectComponent(component, false);
  }

  private getNextNumber(toolName: string, owner:DesignerTab = this.getActiveTab()) {
    for (let i = 1; i < 1000; i++)
      if (document.getElementById(toolName + i) == null && owner[toolName + i] == null)
        return i;
    throw new Error("Could not get the next available component number.");
  }

  redisplayProp(propName: string, value: string) {
    this.tableProps.redisplayProp(propName, value);
    if (propName === "id")
      this.getActiveTab().dataSourcePanel.updateIds();
  }

  selectComponent(component: DesignableObject, add: boolean = false) {
    if (component instanceof DesignerTab || component?.id === "tabHeading" || component?.id === "designerTabHeading")
      return;
    if (component instanceof Component) {
      let parent = component.parent;
      while (parent != null) {
        if (parent instanceof Layout && parent.isNested) {
          component = parent;
          break;
        }
        parent = parent.parent;
      }
    }
    designerHandleComponentSelection(this.selectedComponents, component, add, this.tableProps);
    if (this.selectedComponents.length === 1 && this.selectedComponents[0] instanceof DesignerDataSource && this.tabset.getActiveTab() != null) {
      this.getActiveTab().lastSelectedDataSource = this.selectedComponents[0].designerDataSource;
      this.toolsPanel.displayDataSourceTools();
      this.getActiveTab().dataSourcePanel.buttonModelView.enabled = true;
    }
    else if (this.tabset.getActiveTab() != null && this.selectedComponents.length === 1)
      this.getActiveTab().dataSourcePanel.buttonModelView.enabled = false;
  }

  /**
   * Called by UIDesignerActionHistory when an action happens
   */
  _notifyAction() {
    const undoAction = getDesignerUndoAction(this);
    const redoAction = getDesignerRedoAction(this);
    this.buttonUndo.enabled = undoAction != null;
    this.buttonUndo.tooltip = undoAction == null ? "Nothing to undo" : "Undo: " + undoAction.toString();
    this.buttonRedo.enabled = redoAction != null;
    this.buttonRedo.tooltip = redoAction == null ? "Nothing to redo" : "Redo: " + redoAction.toString();
  }

  toolDropped(tool: string, container: Container, toolProperties): Component {
    this.modified();
    if (toolProperties?.dropped === true) {
      const comp = this.firstSelected;
      comp.parent.remove(comp);
      const copy = cloneComponent(comp);
      copy.id = comp.id;
      this.getActiveTab().designerPanel.add(copy);
      copy.addDropListener(() => this.toolOnDrop(copy));
      copy.addDragStartListener((event) => {
        this.selectedToolProps.dropped = true;
        this.selectComponent(copy, false);
      });
      this.selectComponent(copy, false);
      return comp;
    }
    else {
      let lowerTool = tool.toLowerCase();
      const readOnly = this.toolsPanel.switchDragMode.checked && this.selectedToolProps != null && Object.keys(this.selectedToolProps).length > 1;
      if (readOnly)
        lowerTool = "label";
      const nextNumber = this.getNextNumber(lowerTool);
      const component = ComponentTypes.createComponentOfType(lowerTool, { id: lowerTool + nextNumber });
      component._designer = this;
      component._initialDropInDesigner();
      this.getActiveTab()[lowerTool + nextNumber] = component;
      if ("caption" in component)
        component.caption = lowerTool + nextNumber;
      container._designerDrop(component);
      if (this.selectedToolProps != null) {
        const props = { ...this.selectedToolProps };
        if ("caption" in this.selectedToolProps) {
          if (container instanceof TableCell && container.col != null && container.col.heading.caption.startsWith("Column ")) {
            container.col.heading.caption = this.selectedToolProps.caption;
            container.col.headingCell.caption = this.selectedToolProps.caption;
          }
        }

        if (readOnly)
          delete props.caption;
        component.setProps(props);
        if (container instanceof TableCell && container.col != null ){
          const serialized=serializeComponents(container.components,null);
          const componentDefs=JSON.parse(serialized);
          container.col.cellDef = {  cellProps: { table: container._table}};
          container.col.cellDef.def={type:"cell",components : componentDefs};
        }
      }
      component.addDragStartListener((event) => {
        this.selectedToolProps.dropped = true;
        this.selectComponent(component, false);
      });
      this.selectComponent(component, false);
      return component;
    }
  }

  toolOnDrop(component: Component) {
    if (this.selectedToolProps.dropped === true) {
      if ((component as any).acceptsTool != null && (component as any).acceptsTool(this.selectedTool) && (component as any).parent._designer != null)
        (component as Container).add(this.firstSelected);
    }
    else {
      const toolName = this.selectedTool.toolName;
      const toolLowerCase = toolName.toLowerCase();
      const nextNumber = this.getNextNumber(toolLowerCase);
      const newcomponent = ComponentTypes.createComponentOfType(toolLowerCase, { id: toolLowerCase + nextNumber });
      if ((component as any).acceptsTool != null && (component as any).acceptsTool(this.selectedTool))
        (component as Container).add(newcomponent);
      else
        component.parent.add(newcomponent);

      this.selectComponent(newcomponent, false);
      newcomponent._designer = this;
      this.getActiveTab()[toolLowerCase + nextNumber] = newcomponent;
      if ("caption" in newcomponent)
        newcomponent.caption = toolLowerCase + nextNumber;
      if (this.selectedToolProps != null)
        newcomponent.setProps(this.selectedToolProps);
    }

    this.selectedToolProps.dropped = 'false';
  }

  dragTool(event, tool: DesignerTool, toolProps: any) {
    this.selectTool(tool);
    this.selectedToolProps = toolProps;
    const toolName = this.selectedTool.toolName;
    event.domEvent.dataTransfer.setDragImage(tool.image.element, 0, 0);
  }

  selectTool(tool) {
    if (this.selectedTool != null) {
      this.selectedTool.color = getThemeColor("subtle.darker");
      this.selectedTool.backgroundColor = getThemeColor("defaultBackground");
    }
    this.selectedTool = tool;
    tool.color = getThemeColor("primary.reverse");
    tool.backgroundColor = getThemeColor("primary.light");
  }

  addDesignerContainerProperties(container: Component, minWidth: number, minHeight: number, allowDropFunc: (tool: DesignerTool) => boolean) {
    setClassIncluded(container, designerClasses.designerContainer);
    if (container.width === undefined && container.minWidth === undefined)
      container.element.style.minWidth = minWidth + "px";
    if (container.minHeight === undefined)
      container.element.style.minHeight = minHeight + "px";
    (container as DesignerContainer).acceptsTool = (tool: DesignerTool) => {
      return tool != null && (allowDropFunc == null || allowDropFunc(tool));
    };
    container.addDragOverListener(this.containerDragOverListener);
    container.addDropListener(this.containerDragDropListener);
  }

  displayDataSourceTools() {
    this.toolsPanel.displayDataSourceTools();
  }

  componentDropped(comp: Component) {

  }

  get allowsDrop(): boolean {
    return true;
  }

}
